import React, { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch } from "@fortawesome/free-solid-svg-icons";
import { FaEye } from "react-icons/fa";
import axios from "axios";
import { DataGrid } from "@mui/x-data-grid";
import { toast } from "react-toastify";
import Grid from '../components/Grid';
import { FaTruckLoading } from "react-icons/fa";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { FaDownload } from "react-icons/fa";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

function DispatchedStockOnLoan({onAddReceivedStockOnLoanClick}) {
  const [dispatchedStockOnLoan, setDispatchedStockOnLoan] = useState([]);
  const [selectedDispatchedStockOnLoan, setSelectedDispatchedStockOnLoan] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [userRole, setUserRole] = useState("");
  const [selectedSearchField, setSelectedSearchField] = useState("incomingStockOnLoanCode");

  // console.log("Fetched token from localStorage:", token);

  useEffect(() => {
    const fetchUserRole = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) {
          toast.error("Authentication token is missing. Please log in again.");
          return;
        }
  
        const userResponse = await axios.get(`${API_BASE_URL}/users/specific`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
  
        setUserRole(userResponse.data.role); // Set the user role
      } catch (error) {
        console.error("Error fetching user role:", error);
        toast.error("Error fetching user role.");
      }
    };
  
    fetchUserRole();
  }, []);

  useEffect(() => {
    const fetchDispatchedStockOnLoan = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) {
          toast.error("Authentication token is missing. Please log in again.");
          return;
        }

        let response;
        try {
          // Try new API
          response = await axios.get(`${API_BASE_URL}/dispatchedStockOnLoan/getStatus/org`, {
            headers: { Authorization: `Bearer ${token}` },
          });
        } catch (error) {
          // If new API fails, fallback to old one
          console.warn("Falling back to old incoming API:", error.message);
          response = await axios.get(`${API_BASE_URL}/dispatchedStockOnLoan/incoming`, {
            headers: { Authorization: `Bearer ${token}` },
          });
        }

        const receivedStockOnLoan = response.data.receivedStockOnLoan || [];

        const formatted = receivedStockOnLoan.map((order) => ({
          id: order._id,
          incomingStockOnLoanCode: order.dispatched_stock_on_loan_code || "N/A",
          fromLocation: order.from_location_id?.location_name || "Unknown",
          fromOrganization: order.organization?.organization_name || "Unknown",
          incomingOrganization: order.to_organization_id?.organization_name || "Unknown",
          incomingLocation: order.to_location_id?.location_name || "Unknown",
          createdDate: new Date(order.created_date).toLocaleDateString("en-GB"),
          created_by: order.created_by?.username || "Unknown",
          status: order.status,
          createdAt: order.createdAt,
          originalOrder: order,
        }));

        setDispatchedStockOnLoan(formatted);
      } catch (error) {
        console.error("Error fetching dispatched stock on loan:", error);
        toast.error("Error fetching dispatched stock on loan.");
      }
    };

  fetchDispatchedStockOnLoan();
}, []);

  const handleDispatchedStockOnLoanClick = async (dispatchedStockOnLoan) => {
    console.log("Dispatched Stock On Loan Clicked: ", dispatchedStockOnLoan);
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        toast.error("Authentication token is missing. Please log in again.");
        return;
      }

      let response;
      try {
        // Try new API
        response = await axios.get(
          `${API_BASE_URL}/dispatchedStockOnLoan/getStatus/org/${dispatchedStockOnLoan.id}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
      } catch (error) {
        // Fallback to old API
        console.warn("Falling back to old incoming/:id API:", error.message);
        response = await axios.get(
          `${API_BASE_URL}/dispatchedStockOnLoan/incoming/${dispatchedStockOnLoan.id}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
      }

      const detailedOrder = response.data.incomingStockOnLoan;
      const formattedProducts = detailedOrder.dispatchedStockItems.map((item) => ({
        id: item.product_id._id,
        product_name: item.product_id.product_name,
        product_image: item.product_id.product_image,
        product_code: item.product_id.product_code,
        quantity: item.qty,
        unit: item.product_id.unit?.unit_name,
      }));

      setSelectedDispatchedStockOnLoan({
        ...detailedOrder,
        products: formattedProducts,
        sourceLocation: detailedOrder.from_location_id.location_name,
        destinationOrganization: detailedOrder.to_organization_id.organization_name,
        destinationLocation: detailedOrder.to_location_id.location_name,
        selectedProducts: formattedProducts || [],
      });

    } catch (error) {
      console.error("Error fetching dispatched stock on loan details:", error.message);
      toast.error("Error fetching dispatched stock on loan details.");
    }
  };

  const handleClosePopup = () => {
    setSelectedDispatchedStockOnLoan(null);
  };

  const sortedDispatchedStockOnLoan = [...dispatchedStockOnLoan].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

  const filteredDispatchedStockOnLoan = sortedDispatchedStockOnLoan.filter((order) => {
    const fieldValue = order[selectedSearchField] ? order[selectedSearchField].toString().toLowerCase() : "";
    return fieldValue.includes(searchTerm.toLowerCase());
  });

  const handleAddReceive = (loan) => {
    console.log("Navigating to Add Stock on Loan data:", {
      dispatchedStockOnLoanCode: loan.incomingStockOnLoanCode,
      stockOnLoan: loan,
    });

    // Pass the data to the parent's content change function
    onAddReceivedStockOnLoanClick({
      dispatchedStockOnLoanCode: loan.incomingStockOnLoanCode,
      stockOnLoan: loan,
    });
  };
  const handleDownloadExcel = async () => {
    if (filteredDispatchedStockOnLoan.length === 0) {
      toast.warn("No stock data to export.");
      return;
    }

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Dispatched Stock On Loan");

    const headerRow = [
      "Dispatched Stock On Loan Code",
      "Source Location",
      "Source Organization",
      "Destination Location",
      "Date",
      "Created By",
      "Status",
    ];
    worksheet.addRow(headerRow);

    const header = worksheet.getRow(1);
    header.eachCell((cell) => {
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFD966" },
      };
      cell.font = { bold: true };
      cell.border = {
        top: { style: "thin" },
        left: { style: "thin" },
        bottom: { style: "thin" },
        right: { style: "thin" },
      };
    });

    filteredDispatchedStockOnLoan.forEach((stock) => {
      worksheet.addRow([
        stock.incomingStockOnLoanCode,
        stock.fromLocation,
        stock.fromOrganization,
        stock.incomingLocation,
        stock.createdDate,
        stock.created_by,
        stock.status,
      ]);
    });

    worksheet.columns.forEach((column, i) => {
      let maxLength = headerRow[i].length;
      column.eachCell({ includeEmpty: true }, (cell) => {
        const cellLength = cell.value ? cell.value.toString().length : 0;
        if (cellLength > maxLength) maxLength = cellLength;
      });
      column.width = maxLength < 20 ? 20 : maxLength;
    });

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    saveAs(blob, "incoming_stock_on_loan.xlsx");
  };

  const columns = [
    { field: "incomingStockOnLoanCode", headerName: "Dispatched Stock On Loan Code", flex: 1 },
    { field: "createdDate", headerName: "Create Date", flex: 1 },
    { field: "fromLocation", headerName: "Source Location", flex: 1 },
    { field: "fromOrganization", headerName: "Source Organization", flex: 1 },
    { field: "incomingLocation", headerName: "Destination Location", flex: 1 },
    {
      field: "status",
      headerName: "Status",
      flex: 1,
      renderCell: (params) => {
        const status = params.row.status;
    
        // Map statuses to classes
        const statusClassMap = {
          "TO Created": "bg-blue-500 text-white",
          "Dispatched": "bg-green-500 text-white",
          "Drafted": "bg-lime-600 text-white",
          "Partially Received": "bg-orange-400 text-white",
          "Fully Received": "bg-purple-600 text-white",
        };
    
        // Get the class based on status or default to a neutral color
        const statusClass = statusClassMap[status] || "bg-gray-500 text-white"; // Default class if status not found
    
        return (
          <span className={`px-2 py-1 rounded-sm ${statusClass}`}>
            {status}
          </span>
        );
      },
    },
    { field: "created_by", headerName: "Created By", flex: 1 },
    {
      field: "action",
      headerName: "Action",
      flex: 1,
      renderCell: (params) => {
        const isFullyReceived = params.row.status === "Fully Received";

        const handleClick = () => {
          if (isFullyReceived) {
            toast.info("This stock on loan is already fully received. No further receiving allowed.");
          } else {
            handleAddReceive(params.row);
          }
        };

        return (
          <div className="flex gap-2">
            <button
              onClick={() => handleDispatchedStockOnLoanClick(params.row)}
              className="bg-yellow-500 text-white p-2 flex items-center justify-center rounded"
              style={{ width: "30px", height: "30px" }}
            >
              <FaEye className="text-white" />
            </button>

            <button
              onClick={handleClick}
              className="bg-green-600 text-white p-2 flex items-center justify-center rounded"
              style={{ width: "30px", height: "30px" }}
            >
              <FaTruckLoading />
            </button>
          </div>
        );
      },
    },
  ];

  return (
    <div className="flex h-screen">
      <div className="flex-1 p-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-2xl font-semibold">Incoming Stock on Loan</h1>
        </div>
        <div className="bg-white p-4 rounded-lg shadow mb-8">
          <div className="flex justify-between mb-4 items-center flex-wrap gap-4">
            {/* Left Section: Search filters */}
            <div className="flex items-center flex-wrap gap-4">
              <select
                className="px-4 py-2 border border-gray-300 rounded-lg"
                value={selectedSearchField}
                onChange={(e) => setSelectedSearchField(e.target.value)}
              >
                <option value="incomingStockOnLoanCode">Dispatched Code</option>
                <option value="fromLocation">Source Location</option>
                <option value="fromOrganization">Source Organization</option>
                <option value="incomingLocation">Destination Location</option>
                <option value="createdDate">Date</option>
                <option value="created_by">Created By</option>
                <option value="status">Status</option>
              </select>

              <div className="relative">
                <input
                  type="text"
                  placeholder="Search Transfer Orders"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="px-4 py-2 pl-10 border border-gray-300 rounded-lg w-full max-w-xs"
                />
                <div className="absolute inset-y-0 left-0 flex items-center pl-3">
                  <FontAwesomeIcon icon={faSearch} className="text-gray-500" />
                </div>
              </div>
            </div>

            {/* Right Section: Download Excel */}
            <div>
              <button
                onClick={handleDownloadExcel}
                className="flex items-center bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700"
              >
                <FaDownload className="mr-2" />
                Download Excel
              </button>
            </div>
          </div>
          <Grid rows={filteredDispatchedStockOnLoan} columns={columns} getRowId={(row) => row.id}/>
        </div>
        {selectedDispatchedStockOnLoan && (
          <div className="fixed inset-0 flex items-center justify-center bg-gray-900 bg-opacity-50 z-50">
            <div className="bg-white p-8 rounded-lg shadow-lg w-full md:w-2/3 lg:w-1/2 relative max-h-[90vh] overflow-y-auto">
              <button
                onClick={handleClosePopup}
                className="absolute top-2 right-2 bg-red-500 text-white w-8 h-8 flex justify-center items-center rounded-sm hover:bg-red-700"
              >
                &times;
              </button>
              <h2 className="text-xl font-semibold mb-4">
                {selectedDispatchedStockOnLoan.dispatched_stock_on_loan_code}
              </h2>
              <div className="mb-4">
                <label className="block text-gray-700 font-bold">Source Location</label>
                <input
                  type="text"
                  value={selectedDispatchedStockOnLoan.from_location_id?.location_name || ""}
                  readOnly
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              <div className="mb-4">
                <label className="block text-gray-700 font-bold">Source Organization</label>
                <input
                  type="text"
                  value={selectedDispatchedStockOnLoan.organization?.organization_name || ""}
                  readOnly
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              <div className="mb-4">
                <label className="block text-gray-700 font-bold">Destination Location</label>
                <input
                  type="text"
                  value={selectedDispatchedStockOnLoan.to_location_id?.location_name || ""}
                  readOnly
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              <div className="mb-4">
                <label className="block text-gray-700 font-bold">Created Date</label>
                <input
                  type="text"
                  value={selectedDispatchedStockOnLoan.created_date || ""}
                  readOnly
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              <div className="mb-4">
                <label className="block text-gray-700 font-bold">Created By</label>
                <input
                  type="text"
                  value={selectedDispatchedStockOnLoan.created_by?.username || "Unknown"}
                  readOnly
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              <div className="mb-4">
                <label className="block text-gray-700 font-bold">Status</label> 
                <input
                  type="text"
                  value={selectedDispatchedStockOnLoan.status || "Unknown"}
                  readOnly
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              <div className="mb-4">
                <h3 className="text-lg font-semibold mb-2">
                   Selected Products
                </h3>
                <div style={{ height: 400, width: "100%" }}>
                  <DataGrid
                    rows={selectedDispatchedStockOnLoan?.products || []} // Use the products from selectedProductionBatch
                    columns={[
                      { field: "product_code", headerName: "ID", width: 100 },
                      {
                        field: "product_image",
                        headerName: "Image",
                        width: 150,
                        renderCell: (params) =>
                        params.row.product_image ? (
                          <img
                            src={`${process.env.REACT_APP_IMAGE_BASE_URL}${params.row.product_image}`}
                            alt="Product"
                            className="h-16 w-16 object-cover rounded-md"
                          />
                          ) : (
                            "No Image"
                          ),
                      },
                      {
                        field: "product_name",
                        headerName: "Product Name",
                        width: 150,
                      },
                      {
                        field: "quantity",
                        headerName: "Quantity",
                        width: 150,
                        headerAlign: "right",
                        align: "right"
                      },
                      { 
                          field: "unit", 
                          headerName: "Units", 
                          width: 150,                    
                          headerAlign: "center", 
                          align: "center" 
                        },
                    ]}
                    pageSize={5}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default DispatchedStockOnLoan;